import { logDebug, logError, logWarn } from '@ptek/client-logger';
import {
  useWidgetContext,
  useWidgetEvents,
  useWidgetExpandCollapse,
} from '@ptek/widget-core';
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import * as audioRecorder from '../../../api-clients/audioRecorder';
import { sessionsClient } from '../../../api-clients/sessions';
import {
  persistRecordingLeaseId,
  readOrCreatePersistedRecordingDeviceId,
  readPersistedRecordingLeaseId,
} from '../../../api-clients/recordingLeaseStorage';
import { TAB_IDS } from '../../../constants/app';
import { WIDGET_EVENTS } from '../../../constants/events';
import { STATUS_COLORS } from '../../../constants/statusColors';
import { AppContext } from '../../../context/AppContext';
import { useAutoExportDocument } from '../../../features/Document/hooks/useAutoExportDocument';
import { createDocumentState } from '../../../features/Document/utils/documentContentUtils';
import { useUserpilotTracking } from '../../../hooks';
import { TIMEOUT_ERROR_MESSAGE } from '../../../hooks/useDocumentGeneration';
import { useRecordingStore } from '../../../stores';
import { AmbientScribeContext, Appointment } from '../../../types';
import { SURVEY_CODES, TRACK_EVENTS } from '../../../userpilot';
import {
  getAutoExportConfigFromContext,
  isFeatureEnabled,
} from '../../../utils/feature-util';
import { FEATURE_FLAGS } from '../../../types/featureFlags';
import { formatElapsedMs } from '../../../utils/duration';
import { TranscriptLine } from '../../../types/recorder';
import { TranscriptProcessor } from '../../../utils/transcriptUtils';
import { CONNECTION_STATE } from '../stores/connectionRetryStore';
import { STOP_RECORDING_STATE } from '../stores/stopRecordingStore';
import { useConnectionRetry } from './useConnectionRetry';
import { useStopRecordingStateMachine } from './useStopRecordingStateMachine';
import { useSessionIdResolution } from '../../../hooks/useSessionIdResolution';
import { buildStartSetup } from '../../../utils/buildStartSetup';

/**
 * Ensures an unknown error is converted to an Error object
 */
const toError = (error: unknown): Error => {
  return error instanceof Error ? error : new Error(String(error));
};

export const UPLOAD_DATA_LOSS_MESSAGE =
  "Some audio couldn't be uploaded and may be missing from this recording. Recording continues.";

export const PAUSE_CHECKPOINT_FAILED_MESSAGE =
  "Couldn't pause — your place wasn't saved. Recording is still in progress. Tap pause again to retry.";

export const NO_AUDIO_DETECTED_MESSAGE =
  'No audio detected. Check that your microphone is on and not muted.';

/** Successive accepted silent verdicts that raise {@link NO_AUDIO_DETECTED_MESSAGE}. */
const SILENT_VERDICTS_FOR_BANNER = 2;

const transcriptLineKey = (line: TranscriptLine): string =>
  line.chunkId && line.segmentId
    ? `${line.chunkId}:${line.segmentId}`
    : `${line.chunkId ?? ''}:${line.timestamp}:${line.text}`;

const mergeTranscriptLines = (
  existing: TranscriptLine[],
  incoming: TranscriptLine[]
): TranscriptLine[] => {
  const seen = new Set(existing.map(transcriptLineKey));
  return [
    ...existing,
    ...incoming.filter((line) => {
      const key = transcriptLineKey(line);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    }),
  ];
};

/**
 * Snapshot of the wall-clock timing refs captured at the stop instant, before
 * persistRecordingDuration nulls those refs. Lets the (possibly late) finalize
 * compute the per-session duration from the real stop moment rather than from
 * already-reset refs or a clock that has advanced past the recovery wait.
 */
interface RecordingTimingSnapshot {
  stopAt: number;
  startTime: number | null;
  pausedDuration: number;
  pauseStartTime: number | null;
}

interface UseRecordingProps {
  activeRecordingSession: Appointment | null;
  deviceId?: string;

  onError?: (error: Error) => void;
  handleTabChange: (tab: string) => void;
}

export const useRecording = (props: UseRecordingProps) => {
  const { activeRecordingSession, deviceId, onError, handleTabChange } = props;

  const {
    updateSessionModel,
    generateDocument,
    sessionModel,
    snapshotSelectedTemplate,
    awaitBackgroundNote,
    user,
  } = useContext(AppContext);

  const connectionRetry = useConnectionRetry();
  const stopRecordingStateMachine = useStopRecordingStateMachine();
  const { mintSession } = useSessionIdResolution();

  // Stabilize stopRecordingStateMachine to prevent stopRecording from recreating
  const stopRecordingStateMachineRef = useRef(stopRecordingStateMachine);
  useEffect(() => {
    stopRecordingStateMachineRef.current = stopRecordingStateMachine;
  }, [stopRecordingStateMachine]);

  const [isStartingRecording, setIsStartingRecording] = useState(false);
  const [recordingConflict, setRecordingConflict] = useState<string | null>(
    null
  );
  const [uploadDataLossMessage, setUploadDataLossMessage] = useState<
    string | null
  >(null);
  const [pauseCheckpointFailed, setPauseCheckpointFailed] = useState<
    string | null
  >(null);
  const [noAudioDetectedMessage, setNoAudioDetectedMessage] = useState<
    string | null
  >(null);
  /** The current recording run; a silence verdict from an older run's closure is dropped. */
  const silenceRunTokenRef = useRef(0);
  const silenceVerdictsRef = useRef({
    lastAcceptedChunkNumber: -1,
    successiveSilent: 0,
  });
  const [localRecordingDuration, setLocalRecordingDuration] = useState('00:00');
  const [recordingAudioStream, setRecordingAudioStream] =
    useState<MediaStream | null>(null);

  const recordingStartTimeRef = useRef<number | null>(null);
  const timerIntervalRef = useRef<number | null>(null);
  const pausedDurationRef = useRef<number>(0);
  const pauseStartTimeRef = useRef<number | null>(null);
  // Device the active recording is currently running on (NEB-91363 live-swap).
  const activeRecordingDeviceIdRef = useRef<string | undefined>(undefined);
  const preservedTranscriptKeyRef = useRef<string | null>(null);

  const { emitToHost } = useWidgetEvents();
  const { widgetContext } = useWidgetContext<AmbientScribeContext>();
  const { expand } = useWidgetExpandCollapse();

  const autoExportConfig = getAutoExportConfigFromContext(
    widgetContext.context
  );
  // BG-generation flag: gates the 'finalizing' hold phase shown from Stop until
  // the 202/job_id arrives. Must NOT be the CONNECTION_RETRY flag — the stop
  // state-machine branch can run with bg OFF (sync path).
  const isBackgroundGenerationEnabled = isFeatureEnabled(
    widgetContext.context?.features,
    FEATURE_FLAGS.SCRIBE_BACKGROUND_GENERATION
  );
  const { performAutoExport } = useAutoExportDocument();
  // Read at session creation; a ref keeps the start callbacks' deps unchanged.
  const startSetupInputsRef = useRef<Parameters<typeof buildStartSetup>[0]>({
    template: undefined,
    contextData: [],
    features: undefined,
    brand: undefined,
  });
  // Test doubles pass partial session models without these lists.
  startSetupInputsRef.current = {
    template: sessionModel.templates?.find(
      (template) => template.id === sessionModel.selectedTemplateId
    ),
    contextData: sessionModel.contextData ?? [],
    features: widgetContext.context?.features,
    brand: user?.brand,
  };

  // Use individual selectors to ensure stable references (prevents infinite loops)
  const isRecording = useRecordingStore((state) => state.isRecording);
  const isPaused = useRecordingStore((state) => state.isPaused);
  const setIsRecording = useRecordingStore((state) => state.setIsRecording);
  const setWhisperSessionId = useRecordingStore(
    (state) => state.setWhisperSessionId
  );
  const recordingSessionId = useRecordingStore(
    (state) => state.recordingSessionId
  );
  // Mirror recordingSessionId into a ref so the (possibly late) finalize in
  // onRecordingStopped reads a stable persisted sessionId — the duration key —
  // even after re-renders. recordingSessionId is minted undefined→defined mid
  // recording; finalize runs after stop, so the ref reflects the final value.
  const recordingSessionIdRef = useRef<string | null>(recordingSessionId);
  useEffect(() => {
    recordingSessionIdRef.current = recordingSessionId;
  }, [recordingSessionId]);
  const setRecordingSessionId = useRecordingStore(
    (state) => state.setRecordingSessionId
  );
  const setStopRecordingFn = useRecordingStore(
    (state) => state.setStopRecordingFn
  );
  const setAbortRecordingFn = useRecordingStore(
    (state) => state.setAbortRecordingFn
  );
  const setStartFreshUnifiedSessionFn = useRecordingStore(
    (state) => state.setStartFreshUnifiedSessionFn
  );
  const unifiedSessionIdEnabled = useRecordingStore(
    (state) => state.unifiedSessionIdEnabled
  );
  const hasRecordedInSession = useRecordingStore(
    (state) => state.hasRecordedInSession
  );
  const setHasRecordedInSession = useRecordingStore(
    (state) => state.setHasRecordedInSession
  );
  const setCompletedDuration = useRecordingStore(
    (state) => state.setCompletedDuration
  );
  // Current recording wins. Host/hydrated sessionId is only a saved-session
  // fallback for reopen/selection when there is no current recordingSessionId.
  // That prevents STOP from reading a stale host id, and prevents re-recording
  // over an existing transcript from showing the old transcript's duration.
  const completedDuration = useRecordingStore((state) => {
    if (isRecording) return null;

    if (recordingSessionId) {
      return state.completedDurations[recordingSessionId] ?? null;
    }

    const hostSessionId = activeRecordingSession?.sessionId;
    return hostSessionId
      ? (state.completedDurations[hostSessionId] ?? null)
      : null;
  });

  const { trackEvent, triggerSurvey } = useUserpilotTracking();

  /**
   * Computes the final elapsed time from the authoritative wall-clock refs
   * (start time minus paused time, plus any in-flight pause) and persists it as
   * the completed total for the current recording SESSION (keyed on the
   * persisted recordingSessionId, not the encounter — an encounter can have
   * multiple sessions). This is the single source of truth for a finished
   * session's duration — it does not depend on transcript timestamps, so a
   * single-short-chunk session keeps its real total (NEB-93248) and a
   * switched-away session keeps its own value (NEB-91362).
   */
  const finalizeRecordingDuration = useCallback(
    (timing?: RecordingTimingSnapshot) => {
      const stopAt = timing?.stopAt ?? Date.now();
      const startTime = timing?.startTime ?? recordingStartTimeRef.current;
      const pausedDuration =
        timing?.pausedDuration ?? pausedDurationRef.current;
      const pauseStartTime =
        timing?.pauseStartTime ?? pauseStartTimeRef.current;
      const sessionId = recordingSessionIdRef.current;
      if (!startTime || !sessionId) return;

      const inFlightPauseMs = pauseStartTime ? stopAt - pauseStartTime : 0;
      const elapsedMs = stopAt - startTime - pausedDuration - inFlightPauseMs;

      setCompletedDuration(sessionId, formatElapsedMs(elapsedMs));
    },
    [setCompletedDuration]
  );

  const finalizeRecordingDurationRef = useRef(finalizeRecordingDuration);
  useEffect(() => {
    finalizeRecordingDurationRef.current = finalizeRecordingDuration;
  }, [finalizeRecordingDuration]);

  // Use refs to stabilize functions that change (prevents stopRecording from recreating)
  const trackEventRef = useRef(trackEvent);
  const triggerSurveyRef = useRef(triggerSurvey);
  const generateDocumentRef = useRef(generateDocument);
  const snapshotSelectedTemplateRef = useRef(snapshotSelectedTemplate);
  const awaitBackgroundNoteRef = useRef(awaitBackgroundNote);
  const handleTabChangeRef = useRef(handleTabChange);
  const expandRef = useRef(expand);
  const onErrorRef = useRef(onError);
  const performAutoExportRef = useRef(performAutoExport);
  const autoExportConfigRef = useRef(autoExportConfig);
  const isBackgroundGenerationEnabledRef = useRef(
    isBackgroundGenerationEnabled
  );
  const beginTakeFailedRef = useRef(false);
  const templateSnapshotPersistedRef = useRef(false);

  useEffect(() => {
    trackEventRef.current = trackEvent;
    triggerSurveyRef.current = triggerSurvey;
    generateDocumentRef.current = generateDocument;
    snapshotSelectedTemplateRef.current = snapshotSelectedTemplate;
    awaitBackgroundNoteRef.current = awaitBackgroundNote;
    handleTabChangeRef.current = handleTabChange;
    expandRef.current = expand;
    onErrorRef.current = onError;
    performAutoExportRef.current = performAutoExport;
    autoExportConfigRef.current = autoExportConfig;
    isBackgroundGenerationEnabledRef.current = isBackgroundGenerationEnabled;
  }, [
    trackEvent,
    triggerSurvey,
    generateDocument,
    snapshotSelectedTemplate,
    awaitBackgroundNote,
    handleTabChange,
    expand,
    onError,
    performAutoExport,
    autoExportConfig,
    isBackgroundGenerationEnabled,
  ]);

  const getRecordingDurationSeconds = useCallback((): number | null => {
    if (!recordingStartTimeRef.current) {
      return null;
    }
    let pausedMs = pausedDurationRef.current;
    if (pauseStartTimeRef.current) {
      // Stopped while paused: the in-flight pause interval has not been
      // folded into pausedDurationRef yet (see the isPaused effect below).
      pausedMs += Date.now() - pauseStartTimeRef.current;
    }
    const elapsedMs = Date.now() - recordingStartTimeRef.current - pausedMs;
    return Math.max(1, Math.round(elapsedMs / 1000));
  }, []);

  const persistRecordingDuration = useCallback(async () => {
    const durationSeconds = getRecordingDurationSeconds();
    // Reset the timing refs so a second stop call hits the null guard below
    // instead of re-PUTting an inflated duration. The timer effect
    // re-initializes these refs on the next recording start.
    recordingStartTimeRef.current = null;
    pausedDurationRef.current = 0;
    pauseStartTimeRef.current = null;
    if (!recordingSessionId || durationSeconds === null) {
      return;
    }
    try {
      await sessionsClient.updateSession(recordingSessionId, {
        duration: durationSeconds,
      });
    } catch (error) {
      // Telemetry only — never block or fail the stop-recording flow.
      logError('useRecording', 'Failed to persist recording duration', error);
    }
  }, [getRecordingDurationSeconds, recordingSessionId]);

  const handleTranscriptComplete = useCallback(
    async (transcriptLines: TranscriptLine[]) => {
      try {
        // Read fresh from the store, not from this closure: a quick stop can
        // execute before the render that would have delivered the ids minted
        // during start, and the stale ('') captures would skip the transcript
        // upload and the SAVE_SESSION_ID host emit.
        const { recordingSessionId, whisperSessionId } =
          useRecordingStore.getState();
        if (!recordingSessionId || !whisperSessionId) {
          logError('useRecording', '❌ Missing session IDs or upload function');
          return { transcriptText: '' };
        }

        const transcriptData = {
          segments: TranscriptProcessor.convertToUploadFormat(transcriptLines),
          whisperSessionId,
        };

        // Upload the transcript browser-side unless the server owns it via an
        // established unified take. A begin-take failure leaves takeGeneration
        // null even with the flag on, so this take falls back to the legacy
        // upload path (NEB-100347).
        if (
          !unifiedSessionIdEnabled ||
          audioRecorder.getTakeGeneration() === null
        ) {
          const transcriptKey = `${recordingSessionId}:${audioRecorder.getTakeGeneration() ?? 'legacy'}`;
          if (preservedTranscriptKeyRef.current === transcriptKey) {
            return { transcriptText: '' };
          }

          const uploadResult = await sessionsClient.uploadTranscript(
            recordingSessionId,
            transcriptData
          );
          // Stamp only after a successful upload; a rejected upload must leave
          // the key unset so a later preserve path can retry the transcript.
          preservedTranscriptKeyRef.current = transcriptKey;

          if (uploadResult?.transcriptDocumentId) {
            updateSessionModel((prev) => ({
              ...prev,
              transcriptDocumentId: uploadResult.transcriptDocumentId,
            }));
          }
        }

        const encounterId = widgetContext.context.appointment?.id;
        emitToHost(WIDGET_EVENTS.EMITTER_NAMES.SAVE_SESSION_ID, {
          sessionId: recordingSessionId,
          encounterId,
        });

        return {
          transcriptText:
            TranscriptProcessor.toPlainText(transcriptLines) ?? '',
        };
      } catch (error) {
        logError('useRecording', '❌ Failed to handle transcript:', error);
        onErrorRef.current?.(toError(error));
        return { transcriptText: '' };
      }
    },
    [
      emitToHost,
      unifiedSessionIdEnabled,
      updateSessionModel,
      widgetContext.context.appointment?.id,
    ]
  );

  /** Preserve a completed capture without entering the note-generation stop path. */
  const preserveTranscriptOnly = useCallback(async () => {
    const { recordingSessionId, whisperSessionId } =
      useRecordingStore.getState();
    if (
      !recordingSessionId ||
      !whisperSessionId ||
      (unifiedSessionIdEnabled && audioRecorder.getTakeGeneration() !== null)
    ) {
      return;
    }

    const lines = await audioRecorder.waitForTranscriptCompletion();
    if (lines.length === 0) return;
    await handleTranscriptComplete(lines);
  }, [handleTranscriptComplete, unifiedSessionIdEnabled]);

  const startRecording = useCallback(
    async (displayStream?: MediaStream) => {
      if (isStartingRecording || isRecording) return;

      setIsStartingRecording(true);
      setRecordingConflict(null);
      setUploadDataLossMessage(null);
      setPauseCheckpointFailed(null);
      const silenceRunToken = ++silenceRunTokenRef.current;
      silenceVerdictsRef.current = {
        lastAcceptedChunkNumber: -1,
        successiveSilent: 0,
      };
      setNoAudioDetectedMessage(null);

      connectionRetry?.transitionTo(CONNECTION_STATE.CONNECTING);

      let currentSessionId = recordingSessionId;

      // Unified mode: the id survives re-records, so presence no longer means
      // "restart" — that is signalled explicitly.
      const isUnifiedSession = unifiedSessionIdEnabled && !!recordingSessionId;
      const isRestart = isUnifiedSession
        ? hasRecordedInSession
        : !!recordingSessionId;
      const previousSessionId = recordingSessionId;

      try {
        updateSessionModel((prev) => ({
          ...prev,
          sessionId:
            isUnifiedSession && recordingSessionId ? recordingSessionId : '',
          // Pause/stop close a take, not the encounter-bound session. Keep the
          // durable transcript and prior document revisions so the next take
          // appends instead of silently replacing the session history.
          transcript: isUnifiedSession ? prev.transcript : [],
          documents: isUnifiedSession ? prev.documents : [],
          editOverrides: isUnifiedSession ? prev.editOverrides : undefined,
          transcriptDocumentId: isUnifiedSession
            ? prev.transcriptDocumentId
            : undefined,
          transcriptIsEdited: isUnifiedSession
            ? prev.transcriptIsEdited
            : false,
          transcriptUpdatedAt: isUnifiedSession
            ? prev.transcriptUpdatedAt
            : undefined,
          transcriptUpdatedBy: isUnifiedSession
            ? prev.transcriptUpdatedBy
            : undefined,
          // Clear prior-session generation state so the new recording starts
          // clean — a stale generationError suppresses start/stop spinners
          // for the new session (NEB-96499).
          generationError: false,
          isGenerating: false,
          bgJobPending: false,
        }));

        const sameTransport =
          audioRecorder.getSessionId() === recordingSessionId;
        if (isUnifiedSession && sameTransport) {
          await audioRecorder.resetForNewTake();
        } else {
          await audioRecorder.clearSession();
        }

        const prepareSession = async () => {
          templateSnapshotPersistedRef.current = false;
          const liveRecordingSessionId =
            useRecordingStore.getState().recordingSessionId;
          recordingSessionIdRef.current = liveRecordingSessionId;
          const isUnifiedSession =
            unifiedSessionIdEnabled && !!liveRecordingSessionId;
          currentSessionId = liveRecordingSessionId;

          const startSetup = buildStartSetup(startSetupInputsRef.current);

          if (!isUnifiedSession && activeRecordingSession) {
            const recordingSessionData = {
              patientName:
                activeRecordingSession.patientName || 'Unknown Patient',
              encounterId: activeRecordingSession.id,
              visitDate: new Date().toISOString(),
              visitType: activeRecordingSession.title || 'Recording Session',
              duration: 0,
              provider: 'Current Provider',
              transcript: [],
              startSetup,
            };

            const createdSession =
              await sessionsClient.createSession(recordingSessionData);
            updateSessionModel((prev) => ({
              ...prev,
              sessionId: createdSession.id,
            }));

            currentSessionId = createdSession.id;
            recordingSessionIdRef.current = currentSessionId;
            setRecordingSessionId(currentSessionId);
          }

          // Lazy session resolution deliberately does not mint at widget
          // startup. Record-start is the commitment point for unified sessions;
          // mint here so the durable id exists before any upload can be bound to
          // it. mintSession emits SAVE_SESSION_ID immediately after creation.
          let sessionIdEmittedByMint = false;
          if (!currentSessionId && unifiedSessionIdEnabled) {
            currentSessionId = await mintSession(activeRecordingSession, {
              startSetup,
            });
            recordingSessionIdRef.current = currentSessionId;
            sessionIdEmittedByMint = true;
          }

          // Persist the durable id to the host before capture/stop can race the
          // next React render. This also covers create-at-start recovery when
          // startup session minting did not complete.
          if (currentSessionId && !sessionIdEmittedByMint) {
            emitToHost(WIDGET_EVENTS.EMITTER_NAMES.SAVE_SESSION_ID, {
              sessionId: currentSessionId,
              encounterId: widgetContext.context.appointment?.id,
            });
          }

          // A flag-on startup may still need to create the durable session when
          // bootstrap resolution did not return an id. Every unified take must be
          // registered before audio upload, including that fallback path.
          // A begin-take failure must NOT make the visit unrecordable (NEB-100347):
          // without the stamp the recorder falls back to the legacy upload path, so
          // the audio still reaches the server and only server-side stitching for
          // this take is lost. Letting a transient 5xx reach the outer catch would
          // instead abort the recording entirely.
          if (unifiedSessionIdEnabled && currentSessionId) {
            try {
              const recordingDeviceId =
                readOrCreatePersistedRecordingDeviceId();
              const heldLeaseId =
                audioRecorder.getRecordingLeaseId() ??
                readPersistedRecordingLeaseId(currentSessionId);
              beginTakeFailedRef.current = false;
              const beginResult = await sessionsClient.postBeginTake(
                currentSessionId,
                audioRecorder.getTakeStartChunkNumber(),
                recordingDeviceId ?? undefined,
                heldLeaseId ?? undefined,
                true
              );
              audioRecorder.setTakeGeneration(beginResult.takeGeneration);
              audioRecorder.setRecordingLeaseId(beginResult.leaseId ?? null);
              persistRecordingLeaseId(
                currentSessionId,
                beginResult.leaseId ?? null
              );
              if (beginResult.nextChunkNumber !== undefined) {
                audioRecorder.setServerChunkCursor(beginResult.nextChunkNumber);
              }
              try {
                await snapshotSelectedTemplateRef.current(currentSessionId);
                templateSnapshotPersistedRef.current = true;
              } catch (snapshotError) {
                logError(
                  'useRecording',
                  'Start-of-recording template snapshot failed:',
                  snapshotError
                );
                onErrorRef.current?.(toError(snapshotError));
              }
            } catch (beginError) {
              // 409 is a surfaceable in-use conflict on any leg; non-409 errors
              // are transient and fall back to the legacy upload path.
              if (
                (beginError as Error)?.name === 'SessionRecordingConflictError'
              ) {
                throw beginError;
              }
              beginTakeFailedRef.current = true;
              logError(
                'useRecording',
                'begin-take failed; recording via the legacy upload path',
                beginError
              );
            }
          }
        };

        if (!unifiedSessionIdEnabled) {
          await prepareSession();
        }

        // The server can push this take's final transcript after a patient
        // switch has applied; those lines must not land in the next patient.
        const takeAppointmentId =
          useRecordingStore.getState().activeRecordingSession?.id ?? null;

        await audioRecorder.startRecording({
          deviceId,
          displayStream,
          // Continuous capture (mic-first, AC2): session setup moves off the
          // capture path — the recorder runs it concurrently and drains buffered
          // chunks once it resolves. Flag-off: already awaited above; not passed.
          ...(unifiedSessionIdEnabled
            ? {
                prepareSession,
                onUploadSessionReady: (whisperSessionId: string) =>
                  setWhisperSessionId(whisperSessionId),
                // The session is established AFTER onRecordingStarted set the
                // connection state to READY; a failure would otherwise leave the
                // UI on READY forever (legacy start surfaces this via its outer
                // catch).
                onUploadSessionError: (error: Error) => {
                  if (error.name === 'SessionRecordingConflictError') {
                    setRecordingConflict(error.message);
                    trackEventRef.current(TRACK_EVENTS.RECORDING_CONFLICT, {
                      sessionId: currentSessionId,
                      deviceId,
                      encounterId: activeRecordingSession?.id,
                    });
                  }
                  connectionRetry?.transitionTo(
                    CONNECTION_STATE.ERROR,
                    error.message || 'Failed to start recording'
                  );
                },
                // Upload connectivity during the visit is a connection-state
                // signal, never a fatal recording error (AC3): the queue keeps
                // retrying and the recording continues.
                onUploadConnectionChange: (
                  status: 'degraded' | 'recovered'
                ) => {
                  connectionRetry?.transitionTo(
                    status === 'degraded'
                      ? CONNECTION_STATE.RECONNECTING
                      : CONNECTION_STATE.READY
                  );
                },
                onUploadDataLoss: ({ chunkNumber, bytes, boundBytes }) => {
                  onErrorRef.current?.(
                    new Error(
                      `Audio chunk ${chunkNumber} was dropped after the upload buffer reached its bound`
                    )
                  );
                  trackEventRef.current(TRACK_EVENTS.RECORDING_AUDIO_DROPPED, {
                    sessionId: currentSessionId,
                    chunkNumber,
                    bytes,
                    boundBytes,
                  });
                  setUploadDataLossMessage(UPLOAD_DATA_LOSS_MESSAGE);
                },
                onPauseCheckpointError: (error: Error) => {
                  trackEventRef.current(
                    TRACK_EVENTS.RECORDING_PAUSE_CHECKPOINT_FAILED,
                    {
                      sessionId: currentSessionId,
                      deviceId,
                      encounterId: activeRecordingSession?.id,
                      failureReason: error.message,
                    }
                  );
                  setPauseCheckpointFailed(PAUSE_CHECKPOINT_FAILED_MESSAGE);
                },
                onUploadFailed: ({ chunkNumber, status }) => {
                  // A server rejection, not a buffer-bound eviction — surface the
                  // real cause instead of the "audio dropped" data-loss message.
                  onErrorRef.current?.(
                    new Error(
                      `Audio chunk ${chunkNumber} was rejected by the server${
                        status ? ` (status ${status})` : ''
                      } and could not be uploaded`
                    )
                  );
                  setUploadDataLossMessage(UPLOAD_DATA_LOSS_MESSAGE);
                },
              }
            : {}),
          // Outside the flag spread: both upload paths report verdicts.
          onChunkSilent: (chunkNumber: number, silent: boolean) => {
            const verdicts = silenceVerdictsRef.current;
            if (
              silenceRunToken !== silenceRunTokenRef.current ||
              chunkNumber <= verdicts.lastAcceptedChunkNumber
            ) {
              return;
            }
            verdicts.lastAcceptedChunkNumber = chunkNumber;
            verdicts.successiveSilent = silent
              ? verdicts.successiveSilent + 1
              : 0;
            setNoAudioDetectedMessage(
              verdicts.successiveSilent >= SILENT_VERDICTS_FOR_BANNER
                ? NO_AUDIO_DETECTED_MESSAGE
                : null
            );
          },
          onRecordingStarted: () => {
            setIsRecording(true);
            setHasRecordedInSession(true);
            updateSessionModel((prev) => ({
              ...prev,
              recordingPaused: false,
              // A resumed take is no longer paused; reflect the take that was actually just
              // recorded (current user/device) so a later stop neither strands PAUSED nor
              // fires a spurious 'unknown' resume-confirmation on the next Start.
              lastTake: {
                userId: user?.userId ?? '',
                deviceId: readOrCreatePersistedRecordingDeviceId(),
                pausedAt: null,
              },
            }));
            // Anchor the live-swap effect to the device we actually started on,
            // so it does not fire a redundant swap on the post-start render.
            activeRecordingDeviceIdRef.current = deviceId;
            const sessionId = audioRecorder.getSessionId();
            if (sessionId) setWhisperSessionId(sessionId);

            connectionRetry?.transitionTo(CONNECTION_STATE.READY);

            const stream = audioRecorder.getRecordingStream();
            if (stream) setRecordingAudioStream(stream);

            const deviceLabel =
              stream?.getAudioTracks()[0]?.label || 'Unknown Device';

            if (isRestart) {
              trackEvent(TRACK_EVENTS.RECORDING_RESTARTED, {
                sessionId: currentSessionId,
                encounterId: activeRecordingSession?.id,
                previousSessionId: previousSessionId || undefined,
              });
            }

            trackEvent(TRACK_EVENTS.RECORDING_STARTED, {
              sessionId: currentSessionId,
              deviceId,
              deviceLabel,
              templateId: sessionModel.selectedTemplateId,
              //only way we can include name right now
              templateName: sessionModel.templates.find(
                (template) => template.id === sessionModel.selectedTemplateId
              )?.name,
            });
          },
          onRecordingError: (error) => {
            const isTerminal = (() => {
              const state = useRecordingStore.getState();
              return state.isRecording && !state.isPaused;
            })();
            setIsRecording(false);
            // Fatal recorder errors are followed by the module's stopRecording;
            // failed resume is paused and therefore excluded above.
            if (isTerminal) void preserveTranscriptOnly();
            onError?.(error);
          },
          // Failed live device swap: surface a VISIBLE error but keep recording
          // running on the prior device (do NOT setIsRecording(false)).
          onSwitchDeviceError: (error) => {
            onErrorRef.current?.(error);
          },

          onSetLiveTranscript: (transcriptLines) => {
            if (
              takeAppointmentId &&
              useRecordingStore.getState().activeRecordingSession?.id !==
                takeAppointmentId
            ) {
              return;
            }
            const convertedLines: TranscriptLine[] = transcriptLines.map(
              (line) => ({
                timestamp: line.timestamp,
                text: line.text,
                confidence: line.confidence,
                chunkId: line.chunkId,
                segmentId: line.segmentId,
              })
            );

            updateSessionModel((prev) => {
              const newTranscript = mergeTranscriptLines(
                prev.transcript,
                convertedLines
              );

              return {
                ...prev,
                transcript: newTranscript,
              };
            });
          },
          onReplaceLiveTranscript: (transcriptLines) => {
            if (
              takeAppointmentId &&
              useRecordingStore.getState().activeRecordingSession?.id !==
                takeAppointmentId
            ) {
              return;
            }
            updateSessionModel((prev) => ({
              ...prev,
              transcript: transcriptLines,
            }));
          },
        });
      } catch (error) {
        logError('useRecording', '❌ startRecording failed:', error);
        displayStream?.getTracks().forEach((track) => track.stop());
        setIsRecording(false);
        const err = toError(error);

        if (err.name === 'SessionRecordingConflictError') {
          setRecordingConflict(err.message);
          trackEventRef.current(TRACK_EVENTS.RECORDING_CONFLICT, {
            sessionId: recordingSessionId,
            deviceId,
            encounterId: activeRecordingSession?.id,
          });
        }

        connectionRetry?.transitionTo(
          CONNECTION_STATE.ERROR,
          err.message || 'Failed to start recording'
        );

        onError?.(err);
      } finally {
        setIsStartingRecording(false);
      }
    },
    [
      deviceId,
      onError,
      setRecordingSessionId,
      isRecording,
      isStartingRecording,
      activeRecordingSession,
      setWhisperSessionId,
      setIsRecording,
      updateSessionModel,
      trackEvent,
      recordingSessionId,
      sessionModel.selectedTemplateId,
      connectionRetry,
      unifiedSessionIdEnabled,
      hasRecordedInSession,
      setHasRecordedInSession,
      emitToHost,
      widgetContext.context.appointment?.id,
      mintSession,
      preserveTranscriptOnly,
    ]
  );

  const stopRecording = useCallback(
    async (onComplete?: (success: boolean) => void) => {
      // Snapshot the wall-clock timing ONCE, before any await and before
      // persistRecordingDuration nulls the timing refs. The per-session
      // finalize runs late (after the ~5s transcript-recovery wait on the
      // ERROR_STOPPING/ERROR_TRANSCRIPT path and after the refs are reset), so
      // it must compute from this snapshot — not from Date.now() or the refs —
      // to exclude the recovery wait from the persisted duration (B1).
      const timingSnapshot: RecordingTimingSnapshot = {
        stopAt: Date.now(),
        startTime: recordingStartTimeRef.current,
        pausedDuration: pausedDurationRef.current,
        pauseStartTime: pauseStartTimeRef.current,
      };

      // Kick off the duration PUT immediately but do NOT block the mic stop
      // on it. It is awaited again right before document generation so the
      // backend's session read (latency metric) happens strictly after the
      // PUT completes. persistRecordingDuration never rejects (internal catch).
      // It also synchronously nulls the timing refs (hence the snapshot above).
      const persistDurationPromise = persistRecordingDuration();

      logDebug('useRecording', '🛑 stopRecording called', {
        sessionId: recordingSessionId,
        hasStateMachine: !!stopRecordingStateMachineRef.current,
      });

      trackEventRef.current(TRACK_EVENTS.RECORDING_STOPPED, {
        sessionId: recordingSessionId,
      });

      // Stop releases the server-side recording lease; drop the persisted
      // resume-hint so the released id is not re-presented as existingLeaseId
      // on the next begin-take (the recorder also forgets its in-memory copy).
      if (recordingSessionId) {
        persistRecordingLeaseId(recordingSessionId, null);
      }

      // Phase 2a: the server owns durable transcript persistence, but automatic
      // note generation has not shipped yet. Stop cleanly in both retry modes,
      // leave the UI non-generating, and show the transcript tab. The sweep is
      // the backstop for any final-signal or transcript-tail failure.
      // Gate on the ACTUAL take mode, not the raw flag: a
      // begin-take failure leaves takeGeneration null even with the flag on, and
      // that take must fall through to the complete legacy stop path below —
      // otherwise the unified path would await docgen for a take the server
      // never registered (NEB-100347).
      if (
        unifiedSessionIdEnabled &&
        audioRecorder.getTakeGeneration() !== null
      ) {
        // Re-snapshot the latest selection BEFORE emitting any final signal.
        // This both retries a failed start snapshot and fences an in-flight
        // mid-recording selection with a newer version, so finalization cannot
        // race ahead using a prior template.
        if (recordingSessionId) {
          try {
            await snapshotSelectedTemplateRef.current(recordingSessionId);
            templateSnapshotPersistedRef.current = true;
          } catch (snapshotError) {
            logError(
              'useRecording',
              'Pre-stop template snapshot retry failed:',
              snapshotError
            );
            onErrorRef.current?.(toError(snapshotError));
          }
        }
        let transcriptData: { transcriptText: string } = { transcriptText: '' };
        if (stopRecordingStateMachineRef.current) {
          transcriptData =
            (await stopRecordingStateMachineRef.current.executeStopRecording({
              onRecordingStopped: () => {
                finalizeRecordingDurationRef.current(timingSnapshot);
                setIsRecording(false);
                setRecordingAudioStream(null);
              },
              onRecordingError: (error) => {
                logError(
                  'useRecording',
                  'Unified stop deferred to server recovery:',
                  error
                );
              },
              handleTranscriptComplete,
            })) || transcriptData;
          stopRecordingStateMachineRef.current.reset();
        } else {
          await audioRecorder.stopRecording({
            onRecordingStopped: () => {
              finalizeRecordingDurationRef.current(timingSnapshot);
              setIsRecording(false);
              setRecordingAudioStream(null);
            },
            onRecordingError: (error) => {
              logError(
                'useRecording',
                'Unified stop deferred to server recovery:',
                error
              );
            },
          });
          const lines = await audioRecorder.waitForTranscriptCompletion();
          transcriptData = await handleTranscriptComplete(lines);
        }

        // Non-continuous MediaRecorder.stop() queues the final slice's onstop
        // callback. Wait for that slice to upload or be dropped before using
        // hasUploadedAudioChunk() to decide whether server docgen can start;
        // otherwise a quick stop can be mistaken for a no-audio recording.
        await audioRecorder.waitForFinalSliceSettlement();

        finalizeRecordingDurationRef.current(timingSnapshot);
        setIsRecording(false);
        setRecordingAudioStream(null);
        const hasUploadedAudio = audioRecorder.hasUploadedAudioChunk();
        if (hasUploadedAudio) {
          updateSessionModel((prev) => ({
            ...prev,
            isGenerating: false,
            bgJobPending: true,
            generationError: false,
            generationPhase: 'generating',
          }));
          if (!autoExportConfigRef.current?.skipDocumentTabNavigation) {
            const hideSoapNotesTab =
              autoExportConfigRef.current?.hideSoapNotesTab === true;
            handleTabChangeRef.current(
              hideSoapNotesTab ? TAB_IDS.transcript : TAB_IDS.document
            );
          }
        }
        await persistDurationPromise;
        // True only if the note was already COMPLETED at the reconcile inside
        // awaitBackgroundNote; a later render arrives asynchronously via poll/WS.
        let alreadyRendered = false;
        if (hasUploadedAudio && !templateSnapshotPersistedRef.current) {
          // Finalize will record MISSING_TEMPLATE_SNAPSHOT; nothing to await.
          updateSessionModel((prev) => ({
            ...prev,
            isGenerating: false,
            bgJobPending: false,
            generationError: true,
            generationPhase: null,
          }));
        } else if (recordingSessionId) {
          alreadyRendered =
            await awaitBackgroundNoteRef.current(recordingSessionId);
        }
        if (!hasUploadedAudio) {
          updateSessionModel((prev) => ({
            ...prev,
            isGenerating: false,
            generationError: false,
            bgJobPending: false,
            generationPhase: null,
          }));
        }
        // Honor the auto-export tab gate: when auto-export will drive the tab
        // (skipDocumentTabNavigation), do not force a tab change here.
        if (
          !hasUploadedAudio &&
          !alreadyRendered &&
          (!autoExportConfigRef.current?.autoExportAfterGeneration ||
            !autoExportConfigRef.current.skipDocumentTabNavigation)
        ) {
          handleTabChangeRef.current(TAB_IDS.transcript);
        }
        expandRef.current();
        trackEventRef.current(TRACK_EVENTS.RECORDING_COMPLETED, {
          sessionId: recordingSessionId,
          transcriptLength: transcriptData.transcriptText.length,
          wordCount: transcriptData.transcriptText
            ? transcriptData.transcriptText.split(/\s+/).length
            : 0,
        });
        triggerSurveyRef.current(SURVEY_CODES.POST_RECORDING_FEEDBACK);
        onComplete?.(true);
        return transcriptData;
      }

      // NEB-100535 backstop: the take-local unified stop path above only runs
      // when this device owns the take generation. On a unified session, when we
      // know the recording session but hold no local take (e.g. a session
      // recorded on another device is being stopped/resumed here), the exact-job
      // poll can never arm, so a note the server generates for this session would
      // never surface in the open widget. Stop cleanly and hand off to the
      // session-scoped background poll, which awaitBackgroundNote arms when there
      // is no local take. Gated on unified sessions (unifiedSessionIdEnabled) so a
      // flag-off stop still falls through to the legacy path below unchanged.
      if (
        unifiedSessionIdEnabled &&
        recordingSessionId &&
        audioRecorder.getTakeGeneration() === null &&
        // A failed begin-take fell back to legacy upload: no server take, no job to await.
        !beginTakeFailedRef.current
      ) {
        await audioRecorder.stopRecording({
          onRecordingStopped: () => {
            finalizeRecordingDurationRef.current(timingSnapshot);
            setIsRecording(false);
            setRecordingAudioStream(null);
          },
          onRecordingError: (error) => {
            logError(
              'useRecording',
              'Backstop stop deferred to server recovery:',
              error
            );
          },
        });
        finalizeRecordingDurationRef.current(timingSnapshot);
        setIsRecording(false);
        setRecordingAudioStream(null);
        const hasUploadedAudio = audioRecorder.hasUploadedAudioChunk();
        if (hasUploadedAudio) {
          updateSessionModel((prev) => ({
            ...prev,
            isGenerating: false,
            bgJobPending: true,
            generationError: false,
            generationPhase: 'generating',
          }));
          if (!autoExportConfigRef.current?.skipDocumentTabNavigation) {
            const hideSoapNotesTab =
              autoExportConfigRef.current?.hideSoapNotesTab === true;
            handleTabChangeRef.current(
              hideSoapNotesTab ? TAB_IDS.transcript : TAB_IDS.document
            );
          }
        }
        await persistDurationPromise;
        if (hasUploadedAudio) {
          await awaitBackgroundNoteRef.current(recordingSessionId);
        } else {
          updateSessionModel((prev) => ({
            ...prev,
            isGenerating: false,
            generationError: false,
            bgJobPending: false,
            generationPhase: null,
          }));
          if (
            !autoExportConfigRef.current?.autoExportAfterGeneration ||
            !autoExportConfigRef.current.skipDocumentTabNavigation
          ) {
            handleTabChangeRef.current(TAB_IDS.transcript);
          }
        }
        expandRef.current();
        onComplete?.(true);
        return { transcriptText: '' };
      }

      if (stopRecordingStateMachineRef.current) {
        logDebug('useRecording', '🔄 Using state machine for stop recording');
        try {
          const transcriptData =
            await stopRecordingStateMachineRef.current.executeStopRecording({
              onRecordingStopped: () => {
                finalizeRecordingDurationRef.current(timingSnapshot);
                setIsRecording(false);
                setRecordingAudioStream(null);
              },
              onRecordingError: (error) => {
                onErrorRef.current?.(error);
              },
              handleTranscriptComplete,
            });

          const currentState = stopRecordingStateMachineRef.current.state;
          const isErrorState =
            currentState === STOP_RECORDING_STATE.ERROR_STOPPING ||
            currentState === STOP_RECORDING_STATE.ERROR_TRANSCRIPT;

          logDebug('useRecording', '📊 State machine completed', {
            state: currentState,
            isErrorState,
            hasTranscript: !!transcriptData?.transcriptText,
            transcriptLength: transcriptData?.transcriptText?.length || 0,
          });

          if (isErrorState) {
            // State machine encountered an error during stop recording flow
            // Both ERROR_STOPPING and ERROR_TRANSCRIPT are handled the same way:
            // 1. Extract any available transcript data (partial or complete)
            // 2. Stop recording (media already released by audioRecorder)
            // 3. Continue to document generation with available transcript

            const errorStateLabel =
              currentState === STOP_RECORDING_STATE.ERROR_STOPPING
                ? 'ERROR_STOPPING'
                : 'ERROR_TRANSCRIPT';

            const errorContext =
              currentState === STOP_RECORDING_STATE.ERROR_STOPPING
                ? 'Final chunk upload failed'
                : 'Transcript processing failed';

            logError(
              'useRecording',
              `❌ ${errorStateLabel}: ${errorContext}, attempting to save partial transcript`,
              {
                errorMessage: stopRecordingStateMachineRef.current.errorMessage,
                sessionId: recordingSessionId,
              }
            );

            try {
              const partialTranscript =
                await audioRecorder.waitForTranscriptCompletion();

              if (partialTranscript.length > 0) {
                logDebug('useRecording', '📝 Saving partial transcript', {
                  lineCount: partialTranscript.length,
                  sessionId: recordingSessionId,
                  errorState: errorStateLabel,
                });

                const partialTranscriptData =
                  await handleTranscriptComplete(partialTranscript);

                useRecordingStore.getState().setSavedTranscriptData({
                  segments:
                    TranscriptProcessor.convertToUploadFormat(
                      partialTranscript
                    ),
                  transcriptText: partialTranscriptData.transcriptText,
                });

                logDebug(
                  'useRecording',
                  '✅ Partial transcript saved successfully',
                  {
                    transcriptLength:
                      partialTranscriptData.transcriptText.length,
                    hasSavedTranscript: true,
                  }
                );
              } else {
                logError('useRecording', '⚠️ No partial transcript available');
              }
            } catch (transcriptError) {
              logError(
                'useRecording',
                `❌ Failed to extract partial transcript from ${errorStateLabel}`,
                transcriptError
              );
            }

            // Common error handling: recording is stopped, continue to document generation
            finalizeRecordingDurationRef.current(timingSnapshot);
            setIsRecording(false);
            useRecordingStore.getState().setIsPaused(false);

            const errorMessage =
              stopRecordingStateMachineRef.current.errorMessage ||
              'Failed to stop recording';
            const err = new Error(errorMessage);
            onErrorRef.current?.(err);
            onComplete?.(false);

            // Error has been handled (transcript saved, document generation proceeding)
            // so UI should return to normal state
            stopRecordingStateMachineRef.current.reset();

            // Don't return - fall through to document generation with available transcript
          }

          // For error states, we still attempt document generation with available transcript
          logDebug(
            'useRecording',
            '🎨 Updating UI state for document generation'
          );
          setRecordingAudioStream(null);
          updateSessionModel((prev) => ({
            ...prev,
            isGenerating: true,
            bgJobPending: false,
            // Background-generation stops hold generationPhase at 'finalizing' until the 202/job_id; sync stops leave it unset.
            generationPhase: isBackgroundGenerationEnabledRef.current
              ? 'finalizing'
              : undefined,
          }));

          if (autoExportConfigRef.current?.autoExportAfterGeneration) {
            // Auto-export enabled: respect skipDocumentTabNavigation and hideSoapNotesTab
            if (!autoExportConfigRef.current.skipDocumentTabNavigation) {
              const hideSoapNotesTab =
                autoExportConfigRef.current.hideSoapNotesTab === true;
              handleTabChangeRef.current(
                hideSoapNotesTab ? TAB_IDS.transcript : TAB_IDS.document
              );
            }
          } else {
            handleTabChangeRef.current(TAB_IDS.document);
          }
          expandRef.current();

          // For error states (ERROR_STOPPING/ERROR_TRANSCRIPT), use saved transcript data
          const transcriptText =
            transcriptData?.transcriptText ||
            useRecordingStore.getState().savedTranscriptData?.transcriptText;

          logDebug(
            'useRecording',
            '📝 Transcript data for document generation',
            {
              hasTranscript: !!transcriptText,
              transcriptLength: transcriptText?.length || 0,
              source: transcriptData?.transcriptText
                ? 'state-machine'
                : 'saved-data',
            }
          );

          if (transcriptText) {
            try {
              logDebug('useRecording', '🚀 Starting document generation');
              // Await the duration PUT first: document generation reads the session's persisted duration.
              await persistDurationPromise;
              const generatedDocument = await generateDocumentRef.current({
                transcript: transcriptText,
              });

              logDebug('useRecording', '✅ Document generation completed');

              trackEventRef.current(TRACK_EVENTS.RECORDING_COMPLETED, {
                sessionId: recordingSessionId,
                transcriptLength: transcriptText.length,
                wordCount: transcriptText.split(/\s+/).length,
              });

              triggerSurveyRef.current(SURVEY_CODES.POST_RECORDING_FEEDBACK);

              if (
                autoExportConfigRef.current?.autoExportAfterGeneration &&
                generatedDocument
              ) {
                logDebug('useRecording', '🔄 Performing auto-export');
                const documentState = createDocumentState(generatedDocument);
                performAutoExportRef.current(documentState);
              }
            } catch (error) {
              const isTimeout =
                error instanceof Error &&
                error.message === TIMEOUT_ERROR_MESSAGE;
              logError('useRecording', '❌ Document generation failed:', {
                isTimeout,
                error,
              });

              updateSessionModel((prev) => ({
                ...prev,
                isGenerating: false,
                generationError: true,
                bgJobPending: false,
                generationPhase: null,
              }));
            }
          } else {
            logError(
              'useRecording',
              '❌ No transcript available for document generation',
              {
                sessionId: recordingSessionId,
              }
            );
            updateSessionModel((prev) => ({
              ...prev,
              isGenerating: false,
              bgJobPending: false,
              generationPhase: null,
            }));
          }

          const isSuccess =
            currentState === STOP_RECORDING_STATE.SUCCESS ||
            !!transcriptData?.transcriptText;

          logDebug('useRecording', '🏁 Stop recording completed', {
            success: isSuccess,
            finalState: currentState,
          });

          onComplete?.(isSuccess);
          return transcriptData || { transcriptText: '' };
        } catch (error) {
          logError('useRecording', '❌ Stop recording threw exception:', error);
          setIsRecording(false);
          const err = error instanceof Error ? error : new Error(String(error));
          onErrorRef.current?.(err);
          onComplete?.(false);

          return { transcriptText: '' };
        }
      }

      try {
        await audioRecorder.stopRecording({
          onRecordingStopped: () => {
            finalizeRecordingDurationRef.current(timingSnapshot);
            setIsRecording(false);
            setRecordingAudioStream(null);
          },
          onRecordingError: (error) => {
            onErrorRef.current?.(error);
          },
        });

        finalizeRecordingDurationRef.current(timingSnapshot);
        setIsRecording(false);
        setRecordingAudioStream(null);
        updateSessionModel((prev) => ({
          ...prev,
          isGenerating: true,
          bgJobPending: false,
          // Background-generation stops hold generationPhase at 'finalizing' until the 202/job_id; sync stops leave it unset.
          generationPhase: isBackgroundGenerationEnabledRef.current
            ? 'finalizing'
            : undefined,
        }));

        if (autoExportConfigRef.current?.autoExportAfterGeneration) {
          // Auto-export enabled: respect skipDocumentTabNavigation and hideSoapNotesTab
          if (!autoExportConfigRef.current.skipDocumentTabNavigation) {
            const hideSoapNotesTab =
              autoExportConfigRef.current.hideSoapNotesTab === true;
            handleTabChangeRef.current(
              hideSoapNotesTab ? TAB_IDS.transcript : TAB_IDS.document
            );
          }
        } else {
          handleTabChangeRef.current(TAB_IDS.document);
        }
        expandRef.current();

        const transcriptLines =
          await audioRecorder.waitForTranscriptCompletion();
        const transcriptData = await handleTranscriptComplete(transcriptLines);

        if (transcriptData?.transcriptText) {
          try {
            // Await the duration PUT first: document generation reads the session's persisted duration.
            await persistDurationPromise;
            const generatedDocument = await generateDocumentRef.current({
              transcript: transcriptData.transcriptText,
            });

            trackEventRef.current(TRACK_EVENTS.RECORDING_COMPLETED, {
              sessionId: recordingSessionId,
              transcriptLength: transcriptData.transcriptText.length,
              wordCount: transcriptData.transcriptText.split(/\s+/).length,
            });

            triggerSurveyRef.current(SURVEY_CODES.POST_RECORDING_FEEDBACK);

            if (
              autoExportConfigRef.current?.autoExportAfterGeneration &&
              generatedDocument
            ) {
              logDebug('useRecording', '🔄 Performing auto-export');
              const documentState = createDocumentState(generatedDocument);
              performAutoExportRef.current(documentState);
            }

            onComplete?.(true);
          } catch (error) {
            const isTimeout =
              error instanceof Error && error.message === TIMEOUT_ERROR_MESSAGE;
            logError('useRecording', 'Document generation failed:', {
              isTimeout,
              error,
            });

            updateSessionModel((prev) => ({
              ...prev,
              isGenerating: false,
              generationError: true,
              bgJobPending: false,
              generationPhase: null,
            }));

            onComplete?.(false);
          }
        } else {
          // No transcript available: reset the spinner and surface an error so
          // the user is never left with an indefinite "generating" state.
          logError(
            'useRecording',
            '❌ No transcript available for document generation',
            { sessionId: recordingSessionId }
          );

          updateSessionModel((prev) => ({
            ...prev,
            isGenerating: false,
            generationError: true,
            bgJobPending: false,
            generationPhase: null,
          }));

          onErrorRef.current?.(
            new Error('No transcript available for document generation')
          );
          onComplete?.(false);
        }

        return transcriptData || { transcriptText: '' };
      } catch (error) {
        setIsRecording(false);
        const err = toError(error);
        onErrorRef.current?.(err);
        onComplete?.(false);

        return { transcriptText: '' };
      }
    },
    [
      handleTranscriptComplete,
      persistRecordingDuration,
      recordingSessionId,
      setIsRecording,
      unifiedSessionIdEnabled,
    ]
  );

  const pauseRecording = useCallback(() => {
    if (isRecording && !isPaused) {
      setPauseCheckpointFailed(null);
      audioRecorder.pauseRecording();

      trackEvent(TRACK_EVENTS.RECORDING_PAUSED, {
        sessionId: recordingSessionId,
      });
    }
  }, [
    isRecording,
    isPaused,
    trackEvent,
    TRACK_EVENTS.RECORDING_PAUSED,
    recordingSessionId,
  ]);

  const resumeRecording = useCallback(() => {
    if (isRecording && isPaused) {
      setRecordingConflict(null);
      silenceVerdictsRef.current.successiveSilent = 0;
      setNoAudioDetectedMessage(null);
      const resumed = audioRecorder.resumeRecording({
        onTimerUpdate: (elapsedMs: number) => {
          setLocalRecordingDuration(formatElapsedMs(elapsedMs));
        },
      });
      if (resumed) {
        trackEvent(TRACK_EVENTS.RECORDING_RESUMED, {
          sessionId: recordingSessionId,
        });
      }
    }
  }, [isRecording, isPaused, recordingSessionId, trackEvent]);

  const abortRecording = useCallback(async () => {
    await audioRecorder.abortRecording();
    await preserveTranscriptOnly();
    setIsRecording(false);
    setRecordingAudioStream(null);
  }, [preserveTranscriptOnly, setIsRecording]);

  /** Discard this association locally and mint a new session for the encounter. */
  const startFreshUnifiedSession = useCallback(async (): Promise<string> => {
    const oldSessionId = useRecordingStore.getState().recordingSessionId;
    const leaseId =
      audioRecorder.getRecordingLeaseId() ??
      (oldSessionId ? readPersistedRecordingLeaseId(oldSessionId) : null);

    try {
      // Mint first: nothing is torn down yet, so a mint/createSession failure
      // leaves the old session fully intact (the catch below surfaces it).
      // forceNew skips mintSession's adopt-guard and store/host writes — this
      // path owns the store-set + host emit once teardown has succeeded.
      const newSessionId = await mintSession(activeRecordingSession, {
        forceNew: true,
        startSetup: buildStartSetup(startSetupInputsRef.current),
      });

      if (oldSessionId && leaseId) {
        try {
          await sessionsClient.postRecordingComplete(oldSessionId, {
            leaseId,
            leaseTakeGeneration: audioRecorder.getTakeGeneration() ?? undefined,
            action: 'stop',
          });
        } catch (finalizeError) {
          // Post-Stop discard re-finalizes an already-stopped session, which
          // 409s (ACTIVE_RECORDER). Tolerate only that and continue; any other
          // failure is a real finalize error and must surface.
          if (
            (finalizeError as Error)?.name !== 'SessionRecordingConflictError'
          ) {
            throw finalizeError;
          }
          logWarn(
            'useRecording',
            'Old session already finalized (409); proceeding with discard'
          );
        }
        persistRecordingLeaseId(oldSessionId, null);
      }
      audioRecorder.setRecordingLeaseId(null);

      await audioRecorder.clearSession();

      setIsRecording(false);
      useRecordingStore.getState().setIsPaused(false);
      setWhisperSessionId(null);
      setRecordingSessionId(null);
      useRecordingStore.getState().setSavedTranscriptData(null);
      useRecordingStore.getState().setTranscriptSaved(false);
      setHasRecordedInSession(false);
      useRecordingStore.getState().setIsGeneratingDocument(false);
      setRecordingAudioStream(null);
      activeRecordingDeviceIdRef.current = undefined;
      setLocalRecordingDuration('00:00');
      recordingStartTimeRef.current = null;
      pausedDurationRef.current = 0;
      pauseStartTimeRef.current = null;

      updateSessionModel((prev) => ({
        ...prev,
        sessionId: '',
        transcript: [],
        documents: [],
        editOverrides: undefined,
        transcriptDocumentId: undefined,
        transcriptIsEdited: false,
        transcriptUpdatedAt: undefined,
        transcriptUpdatedBy: undefined,
        lastTake: null,
        activeRecorder: null,
        recordingPaused: false,
        isRecording: false,
        isGenerating: false,
        bgJobPending: false,
        generationPhase: null,
        generationError: false,
      }));

      // Teardown is done — adopt the freshly minted id. mintSession skipped
      // these writes under forceNew, so do the store-set + host emit here.
      setRecordingSessionId(newSessionId);
      emitToHost(WIDGET_EVENTS.EMITTER_NAMES.SAVE_SESSION_ID, {
        sessionId: newSessionId,
        encounterId: activeRecordingSession?.id,
      });
      recordingSessionIdRef.current = newSessionId;
      updateSessionModel((prev) => ({ ...prev, sessionId: newSessionId }));
      return newSessionId;
    } catch (error) {
      const err = toError(error);
      logError('useRecording', 'Failed to start a fresh unified session:', err);
      onError?.(err);
      throw err;
    }
  }, [
    activeRecordingSession,
    emitToHost,
    mintSession,
    onError,
    setHasRecordedInSession,
    setIsRecording,
    setRecordingSessionId,
    setWhisperSessionId,
    updateSessionModel,
  ]);

  const startFreshUnifiedSessionRef = useRef(startFreshUnifiedSession);
  startFreshUnifiedSessionRef.current = startFreshUnifiedSession;

  const handleForceCancelRecording = useCallback(async () => {
    try {
      await audioRecorder.stopRecording();
      setIsRecording(false);
      setRecordingAudioStream(null);
    } catch (error) {
      setIsRecording(false);
      setRecordingAudioStream(null);
      onError?.(toError(error));
    }
  }, [onError, setIsRecording]);

  useEffect(() => {
    if (isRecording && !isPaused && !timerIntervalRef.current) {
      if (!recordingStartTimeRef.current) {
        recordingStartTimeRef.current = Date.now();
        pausedDurationRef.current = 0;
      }

      timerIntervalRef.current = window.setInterval(() => {
        if (recordingStartTimeRef.current) {
          const elapsedMs =
            Date.now() -
            recordingStartTimeRef.current -
            pausedDurationRef.current;
          setLocalRecordingDuration(formatElapsedMs(elapsedMs));
        }
      }, 1000);
    } else if ((isPaused || !isRecording) && timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;

      if (isPaused && !pauseStartTimeRef.current) {
        pauseStartTimeRef.current = Date.now();
      }
    }

    return () => {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }
    };
  }, [isRecording, isPaused]);

  useEffect(() => {
    if (isRecording) {
      if (isPaused && !pauseStartTimeRef.current) {
        pauseStartTimeRef.current = Date.now();
      } else if (!isPaused && pauseStartTimeRef.current) {
        pausedDurationRef.current += Date.now() - pauseStartTimeRef.current;
        pauseStartTimeRef.current = null;
      }
    }
  }, [isPaused, isRecording]);

  useEffect(() => {
    if (isStartingRecording) {
      setLocalRecordingDuration('00:00');
      recordingStartTimeRef.current = null;
      pausedDurationRef.current = 0;
      pauseStartTimeRef.current = null;
    }
  }, [isStartingRecording]);

  // Live mic swap (NEB-91363): re-acquire on the new device and continue
  // recording when deviceId changes mid-recording. Early-returns (zero behavior
  // change) unless actively recording and the device actually changed.
  useEffect(() => {
    if (!isRecording || isPaused || isStartingRecording) return;
    if (deviceId === activeRecordingDeviceIdRef.current) return;

    const previousDeviceId = activeRecordingDeviceIdRef.current;
    activeRecordingDeviceIdRef.current = deviceId;

    audioRecorder
      .switchDevice(deviceId ?? 'default')
      .then(() => {
        // Follow the new mic in the waveform visualizer.
        const stream = audioRecorder.getRecordingStream();
        if (stream) setRecordingAudioStream(stream);
      })
      .catch(() => {
        // A failed/dropped swap already surfaced its single user-facing error
        // via onSwitchDeviceError (acquisition failure) or is a transient
        // double-switch no-op. Only revert the ref to the actually-active device
        // so a subsequent change re-attempts the swap (NEB-91363, A + N1).
        activeRecordingDeviceIdRef.current = previousDeviceId;
      });
  }, [deviceId, isRecording, isPaused, isStartingRecording]);

  useEffect(() => {
    setLocalRecordingDuration('00:00');
    recordingStartTimeRef.current = null;
    pausedDurationRef.current = 0;
    pauseStartTimeRef.current = null;
  }, [activeRecordingSession?.id]);

  const displayDuration = useMemo(() => {
    return localRecordingDuration;
  }, [localRecordingDuration]);

  const stopRecordingRef = useRef(stopRecording);
  stopRecordingRef.current = stopRecording;
  useEffect(() => {
    setStopRecordingFn((onComplete?: (success: boolean) => void) =>
      stopRecordingRef.current(onComplete)
    );

    return () => {
      setStopRecordingFn(null);
    };
  }, [setStopRecordingFn]);

  const abortRecordingRef = useRef(abortRecording);
  abortRecordingRef.current = abortRecording;
  useEffect(() => {
    setAbortRecordingFn(() => abortRecordingRef.current());

    return () => {
      setAbortRecordingFn(null);
    };
  }, [setAbortRecordingFn]);

  useEffect(() => {
    setStartFreshUnifiedSessionFn(() => startFreshUnifiedSessionRef.current());

    return () => {
      setStartFreshUnifiedSessionFn(null);
    };
  }, [setStartFreshUnifiedSessionFn]);

  const recordingStatus = useMemo(() => {
    if (isRecording && isPaused) return 'PAUSED';
    if (isRecording) return 'RECORDING';
    if (isStartingRecording) return 'STARTING...';
    return 'READY';
  }, [isRecording, isPaused, isStartingRecording]);

  const statusColors = useMemo(() => {
    if (isRecording && isPaused) {
      return STATUS_COLORS.paused;
    }
    if (isRecording) {
      return STATUS_COLORS.recording;
    }
    if (isStartingRecording) {
      return STATUS_COLORS.starting;
    }
    return STATUS_COLORS.ready;
  }, [isRecording, isPaused, isStartingRecording]);

  return {
    isRecording,
    isPaused,
    isStartingRecording,

    displayDuration,
    completedDuration,
    recordingStatus,
    statusColors,
    recordingAudioStream,
    recordingConflict,
    uploadDataLossMessage,
    pauseCheckpointFailed,
    noAudioDetectedMessage,

    startRecording,
    stopRecording,
    pauseRecording,
    resumeRecording,
    handleForceCancelRecording,
    abortRecording,
    startFreshUnifiedSession,
    trackEvent,
  };
};
